import { asyncTimeout } from '@utils/common';
import { makeSelectors } from '@utils/css';

const speechButtonSelectors = makeSelectors({
  accordionPanel: "accordion-panel",
  accordionPanelExpandedModifer: "accordion-panel--expanded",
  accordionToggleButton: "accordion-panel--toggle",
  textComponentToggleButton: "text-component--toggle",
  attachedAudioButton: "attached-audio-button",
  attachedAudioPlayer: "attached-audio-player"
});

/**
 * helper that'll iterate over all currently rendered content elements, and
 * returns a reference to the next one that contains content to read out loud.
 */
async function selectNextContentElement(rootSelector, currentElement) {
  const nextComponent = selectNextComponent(rootSelector, currentElement);
  if (!nextComponent) {
    return;
  }

  // if an accordion was found, then we need to make sure that it's expanded and
  // it's content is rendered
  if (nextComponent.classList.contains(speechButtonSelectors.accordionPanel.className)) {
    const toggleButton = nextComponent.querySelector(speechButtonSelectors.accordionToggleButton());
    if (!toggleButton) {
      return selectNextContentElement(rootSelector, nextComponent);
    }

    // if the component hasn't been expanded yet, then do so now by triggering
    // fake click on the header
    if (!nextComponent.classList.contains(speechButtonSelectors.accordionPanelExpandedModifer.className)) {
      toggleButton.click();
      await asyncTimeout(50);
    }
    return selectNextContentElement(rootSelector, nextComponent);
  }

  // if we get here, we're holding a reference to a content element - check to
  // see if there's any text that needs expanding, and then return the reference
  // to this node
  nextComponent.querySelector(speechButtonSelectors.textComponentToggleButton())?.click();
  return nextComponent;
}

/**
 * helper that iterates over all content components and accordions, and
 * gracefully returns a reference to the next one.
 */
function selectNextComponent(rootSelector, currentElement) {
  const elements = document.querySelectorAll(`
    ${rootSelector} [data-content-component="true"],
    ${rootSelector} ${speechButtonSelectors.accordionPanel()}
  `);
  if (elements.length <= 0) {
    const rootElement = document.querySelector(rootSelector);

    // if no actual content elements were found, then try simply reading out
    // the given element
    return rootElement !== currentElement ? rootElement ?? undefined : undefined;
  }
  if (!currentElement) {
    // select the first element if only a single one was found on the
    // current page
    return elements.item(0);
  }

  // ... otherwise select the next sibling (i.e. the next component that isn't
  // nested within the current component)
  for (let i = -1; ++i < elements.length - 1;) {
    if (elements.item(i) === currentElement) {
      let j = i + 1;
      let nextElement = elements.item(j);

      // Once we've found our current element, then find the next element that
      // isn't nested within the current element
      while (nextElement && !currentElement.matches(speechButtonSelectors.accordionPanel()) && currentElement.contains(nextElement) && j < elements.length) {
        nextElement = elements.item(j++);
      }
      return nextElement;
    }
  }
}

export { selectNextContentElement, speechButtonSelectors };
//# sourceMappingURL=selectNextContentElement.js.map
